import { get, post } from "@/api";

export interface ProductGoods {
  id?: number | string;
  title?: string;
  code?: string;
  barcode?: string;
  m_pic?: string;
  category_id?: number | string;
  /** 物资详情 HTML */
  html?: string | null;
  /** 规格信息 JSON（label/value） */
  information?: string | null;
  keywords?: string | null;
  views?: number | string;
  like?: number | string;
  albums?: Array<{ id?: number | string; goods_id?: number | string; url?: string }>;
  [key: string]: unknown;
}

/** 优先规格图片，没有规格图时再用商品图 */
export function resolveSpecImage(
  specPic?: string | null,
  goodsPic?: string | null,
): string {
  const spec = String(specPic || '').trim();
  if (spec) return spec;
  return String(goodsPic || '').trim();
}

/** 规格是否清单外 */
export function isOutsideFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

/** 规格是否基础单位 */
export function isDefaultUnitFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

export interface ProductSupplier {
  id?: number | string;
  name?: string;
  [key: string]: unknown;
}

/** 接口返回的规格物资（含 goods / supplier） */
export interface ProductSpecItem {
  id: number | string;
  goods_id?: number | string;
  category_id?: number | string;
  name?: string;
  price?: number | string;
  unit?: string;
  stock?: number;
  pic?: string | null;
  /** 是否清单外规格 */
  is_outside?: boolean | number | string;
  goods?: ProductGoods | null;
  supplier?: ProductSupplier | null;
  [key: string]: unknown;
}

export interface ProductPagesParams {
  page?: number;
  pagenumber?: number;
  search?: string;
  category_id?: number | string;
  /** 供应商名称模糊查询 */
  supplier_name?: string;
  /** 是否清单外规格：0/1，不传不过滤 */
  is_outside?: number | boolean;
  /** 排序：不传默认销量。sales / new / price_asc / price_desc */
  sortBy?: "sales" | "new" | "price_asc" | "price_desc";
}

export interface ProductPagesResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: ProductSpecItem[];
  total?: number;
  page?: number;
  pagenumber?: number;
}

/** 物资分页列表 */
export function getProductPages(params: ProductPagesParams = {}) {
  return post<ProductPagesResponse>("/platform/product/pages", params);
}

/** 物资采购计划使用情况（详情接口 specs[].purchase_plan） */
export interface ProductPurchasePlanUsage {
  matched?: boolean;
  apply_month?: string;
  flow_item_id?: number | string;
  flow_id?: number | string;
  control_type?: number | string;
  control_type_text?: string;
  /** goods_total=商品总量；goods=限定规格 */
  item_mode?: string;
  /** 当前规格单位：计划量（多单位已按 multiple_unit 换算） */
  plan_quantity?: number;
  used_quantity?: number;
  return_quantity?: number;
  remain_quantity?: number;
  net_used_quantity?: number;
  /** 最小单位口径（下单占用） */
  base_plan_quantity?: number;
  base_used_quantity?: number;
  base_return_quantity?: number;
  base_remain_quantity?: number;
  base_net_used_quantity?: number;
  /** 多单位换算比；非多单位为 1 */
  multiple_unit?: number;
  plan_amount?: number | string;
  used_amount?: number | string;
  return_amount?: number | string;
  remain_amount?: number | string;
  usage_ratio?: number | string;
}

/** 企业物资规格选项 */
export interface ProductSpecOption {
  id: number | string;
  goods_id?: number | string;
  name?: string;
  sku?: string;
  price?: number | string;
  unit?: string;
  factory?: string;
  stock?: number;
  pic?: string | null;
  /** 是否清单外规格 */
  is_outside?: boolean | number | string;
  /** 是否基础单位 */
  is_default_unit?: boolean | number | string;
  /** 多单位换算比例 */
  multiple_unit?: number | string;
  /** 对应基础单位规格 ID */
  multiple_spec_id?: number | string;
  supplier?: ProductSupplier | null;
  /** 采购计划使用情况（科室未开启时为 null） */
  purchase_plan?: ProductPurchasePlanUsage | null;
  [key: string]: unknown;
}

/** 企业物资（含 specs） */
export interface ProductWithSpecs {
  id: number | string;
  goods_id?: number | string;
  goods?: ProductGoods | null;
  specs?: ProductSpecOption[];
  /** 当前账号科室是否开启采购计划 */
  is_purchase_plan_enabled?: boolean;
  purchase_plan_month?: string;
  purchase_plan_month_text?: string;
  purchase_plan_department_id?: number | string;
  purchase_plan_department_name?: string;
  [key: string]: unknown;
}

export interface ProductSpecsParams {
  search?: string;
  goods_id?: number | string;
}

export interface ProductSpecsResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: ProductWithSpecs[];
}

/** 物资与规格联动列表 */
export function getProductSpecs(params: ProductSpecsParams = {}) {
  return post<ProductSpecsResponse>("/platform/product/specs", params);
}

export interface ProductDetailResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: ProductWithSpecs | null;
}

/** 物资详情（企业物资 + goods + specs） */
export function getProductDetail(id: number | string) {
  return get<ProductDetailResponse>(`/platform/product/detail/${id}`);
}

/** 按编码批量匹配结果项 */
export interface ProductCodeMatchItem {
  code: string;
  id: number | string;
  goods_id?: number | string;
  name?: string;
  sku?: string;
  price?: number | string;
  unit?: string;
  pic?: string | null;
  goods?: ProductGoods | null;
  supplier?: ProductSupplier | null;
}

export interface ProductMatchCodesResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: {
    matched?: ProductCodeMatchItem[];
    missed?: string[];
  };
  errors?: Record<string, string[]>;
}

/** 按物资编码批量精确匹配规格 */
export function matchProductCodes(codes: string[]) {
  return post<ProductMatchCodesResponse>("/platform/product/match-codes", { codes });
}

export interface ProductNameMatchQuery {
  /** 商品名称（模糊匹配） */
  title: string;
  /** 规格型号（精确匹配；不限定规格时可空） */
  name?: string;
}

export type ProductNameMissReason = 'goods' | 'spec';

export interface ProductNameMissedItem {
  title: string;
  name?: string;
  /** goods: 找不到物资；spec: 物资存在但规格无法匹配 */
  reason?: ProductNameMissReason;
}

export interface ProductNameMatchItem {
  title: string;
  name: string;
  /** 规格 ID；不限定规格时为 0 */
  id?: number | string | null;
  goods_id?: number | string;
  spec_name?: string;
  sku?: string;
  price?: number | string;
  unit?: string;
  pic?: string | null;
  /** 是否清单外规格 */
  is_outside?: boolean | number | string;
  goods?: ProductGoods | null;
  supplier?: ProductSupplier | null;
}

export interface ProductMatchNameResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: {
    matched?: ProductNameMatchItem[];
    missed?: ProductNameMissedItem[];
  };
  errors?: Record<string, string[]>;
}

/** 按商品名称 + 规格型号批量精确匹配 */
export function matchProductNames(items: ProductNameMatchQuery[]) {
  return post<ProductMatchNameResponse>("/platform/product/match-names", { items });
}
